import { Typography, Box } from "@mui/material";
import { ReactElement } from "react";
import { ActiveTags, IStyleTag } from "./ScriptRender";
import {IGrammarMatch, IIgnoredGrammar, IStateComment} from "./Writer";

export type FragmentStyle = ("comment" | "commentHighlight" | "grammar" | "spelling" | "search");

export interface IHighlightOptions {
    comments: IStateComment[]
    commentHighlight?: string
    dictionary: string[]
    grammar: IGrammarMatch[]
    ignoredGrammar: IIgnoredGrammar[]
    searchText?: string
}

export class ScriptTextRender
{
    tagOffset: number = 0;
    constructor(
        private state: string,
        private lineClass: string,
        private classes: { [key in FragmentStyle]: string },
        private onClickFragment: (activeTags: ActiveTags) => ((event: React.MouseEvent<HTMLElement>) => void) | undefined,
        private text: string,
        private highlightOptions: IHighlightOptions) { }

    renderFragment(text: string, fragment: number, offset: number, length: number, activeTags: ActiveTags): ReactElement
    {
        const classNames = [this.lineClass].concat(Object.keys(activeTags).map(style => this.classes[style as FragmentStyle]));
        const title = Object.values(activeTags).map(activeTag => activeTag.tooltip).join("\n");

        this.tagOffset += length;
        return (
            <Typography style={{ fontWeight: "inherit" }} key={`fragment_${++fragment}`} onClick={this.onClickFragment(activeTags)} className={classNames.join(" ")} component="span" title={title}>
                {text.substr(offset, length)}
            </Typography>
        );
    }

    renderAnnotated(text: string, line: number, tags: IStyleTag[], activeTags: ActiveTags): ReactElement
    {
        if (tags.length === 0)
        {
            return (
                <Typography style={{ fontWeight: "inherit" }} key={`line_${line}`} className={this.lineClass} component="span">
                    {text}
                </Typography>
            );
        }
        else
        {
            const elements: ReactElement[] = [];
            let fragment = 0;
            let offset = 0;

            tags.forEach((tag) =>
            {
                if (tag.offset <= text.length)
                {
                    if (offset < tag.offset)
                    {
                        elements.push(this.renderFragment(text, ++fragment, offset, tag.offset - offset, activeTags));
                    }
                    if (tag.type === "begin")
                    {
                        activeTags[tag.details.style as FragmentStyle] = {
                            suggestions: tag.details.suggestions || [],
                            tooltip: tag.details.tooltip || "",
                            offset: tag.details.offset || 0,
                            length: tag.details.length || 0,
                            rule: tag.details.rule ?? "",
                            context: tag.details.context ?? ""
                        };
                    } else
                    {
                        delete activeTags[tag.details.style as FragmentStyle];
                    }
                    offset = tag.offset;
                }
            });
            elements.push(this.renderFragment(text, ++fragment, offset, text.length - offset, activeTags));
            return (
                <>{elements}</>
            );
        }
    }

    renderLine(text: string, line: number, tags: IStyleTag[], activeTags: ActiveTags): ReactElement
    {
        return (
            <Box key={`line_${line}`} className={this.lineClass}>
                {this.renderAnnotated(text, line, tags, activeTags)}
            </Box>
        );
    }

    getStyleTags(text: string, offset: number): IStyleTag[]
    {
        const inRange = (start: number) => start >= offset && start - offset <= text.length;
        const tags: IStyleTag[] = [];
        const { commentHighlight, comments, dictionary, searchText, grammar, ignoredGrammar } = this.highlightOptions;

        comments.forEach((comment) => {
            const style = commentHighlight === comment.comment ? "commentHighlight" : "comment";

            if (inRange(comment.start))
            {
                tags.push({ offset: comment.start - offset, details: { style }, type: "begin" });
            }
            if (inRange(comment.end))
            {
                tags.push({ offset: comment.end - offset, details: { style }, type: "end" });
            }
        });

        if (searchText !== undefined)
        {
            const lowerCaseSearch = searchText.toLowerCase();
            const lowerCaseText = this.text.toLowerCase();
            let searchOffset = offset;
            let start = lowerCaseText.indexOf(lowerCaseSearch, searchOffset);

            while (start >= 0)
            {
                tags.push({ offset: start - offset, details: { style: "search" }, type: "begin" });
                tags.push({ offset: start - offset + searchText.length, details: { style: "search" }, type: "end" });
                searchOffset = start + searchText.length;
                start = lowerCaseText.indexOf(lowerCaseSearch, searchOffset);
            }
        }
        const isIgnored = (match: IGrammarMatch, ignoredGrammar: IIgnoredGrammar[], offset: number): boolean =>
        {
            if (match.offset < offset || text.length < match.offset - offset) {
                return false;
            }
            if (match.shortMessage === "Spelling mistake") {
                const word = text.substr(match.offset - offset, match.length);
                if (dictionary.includes(word)) {
                    return true;
                }
            }
            return ignoredGrammar.find((ignore) =>
            {
                return ignore.rule === match.rule.id && match.context.text === ignore.context
            }) !== undefined;
        }
        grammar && grammar.forEach((match, index) =>
        {
            if (!isIgnored(match, ignoredGrammar || [], offset))
            {
                const ruleEnd = match.offset + match.length;
                const style = match.rule.issueType === "grammar" ? "grammar" : "spelling";

                if (inRange(match.offset))
                {
                    tags.push({
                        offset: match.offset - offset,
                        details: {
                            style,
                            tooltip: match.message,
                            suggestions: match.replacements || [],
                            offset: match.offset,
                            length: match.length,
                            rule: match.rule.id,
                            context: match.context.text
                        },
                        type: "begin"
                    });
                }
                if (inRange(ruleEnd))
                {
                    tags.push({
                        offset: ruleEnd - offset,
                        details: {
                            style,
                            tooltip: match.message,
                            suggestions: match.replacements || [],
                            offset: match.offset,
                            length: match.length,
                            rule: match.message,
                            context: match.context.text
                        },
                        type: "end"
                    });
                }
            }
        });
        return tags.sort((lhs, rhs) => lhs.offset - rhs.offset);
    };
}